import { MetaTags } from '@/components/meta-tags'
import { PlatformsSelectorModal } from '@/components/platform-selector-modal'
import { StyleRecipeModal } from '@/components/style-recipe-modal'
import { buttonVariants } from '@/components/ui/button'
import { coreBenefits } from '@/data/core-benefits'
import { features } from '@/data/features'
import { styleRecipeCategories } from '@/data/style-recipe-categories'
import { getCoverImageSrc, styleRecipesSeed } from '@/data/style-recipes'
import { use_cases } from '@/data/use-cases'
import { cn } from '@/lib/utils'
import { Link, usePage } from '@inertiajs/react'
import { motion } from 'motion/react'
import { useEffect, useRef, useState } from 'react'
import { FaApple } from 'react-icons/fa'
// screenshots
import icon from '@/assets/icon.png'
import picchatCreate from '@/assets/create/picchat-create.avif'
import picchatEditorDark from '@/assets/editor/picchat-editor-inpainting-dark.avif'
import picchatEditor from '@/assets/editor/picchat-editor-inpainting.avif'
import picchatCollectionsDark from '@/assets/picchat-collections-dark.avif'
import picchatEditorCropResize from '@/assets/picchat-editor-crop-resize-social-instagram.avif'
import picchatExtendImages from '@/assets/picchat-editor-outpainting.avif'
import picchatLibrary from '@/assets/picchat-library.avif'
import picchatNew from '@/assets/picchat-new-with-style-recipes-and-style-transfer.avif'
import picchatBulkActions from '@/assets/picchat-organize-bulk-actions.avif'
import { PiApertureDuotone } from 'react-icons/pi'

export default function Home() {
  const {
    title,
    og_title,
    og_type,
    og_url,
    og_image,
    og_description,
    twitter_title,
    twitter_url,
    twitter_image,
    twitter_description,
  } = usePage().props
  const [activeSlide, setActiveSlide] = useState(0)
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const [selectedImageIndices, setSelectedImageIndices] = useState<number[]>([
    0, 0, 0, 0, 0,
  ])

  const scrollToSlide = (index: number) => {
    if (scrollContainerRef.current) {
      const card = scrollContainerRef.current.children[index] as HTMLElement
      if (card) {
        card.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'center',
        })
      }
    }
  }

  const handleScroll = () => {
    if (scrollContainerRef.current) {
      const container = scrollContainerRef.current
      const cards = Array.from(container.children) as HTMLElement[]
      const containerRect = container.getBoundingClientRect()
      const containerCenter = containerRect.left + containerRect.width / 2

      // Find which card's center is closest to the container center
      let closestIndex = 0
      let closestDistance = Infinity

      cards.forEach((card, index) => {
        const cardRect = card.getBoundingClientRect()
        const cardCenter = cardRect.left + cardRect.width / 2
        const distance = Math.abs(containerCenter - cardCenter)
        if (distance < closestDistance) {
          closestDistance = distance
          closestIndex = index
        }
      })

      setActiveSlide(closestIndex)
    }
  }

  useEffect(() => {
    const container = scrollContainerRef.current
    if (container) {
      container.addEventListener('scroll', handleScroll, { passive: true })
      // Initial check
      handleScroll()
      return () => container.removeEventListener('scroll', handleScroll)
    }
  }, [])

  const beta = true

  return (
    <>
      <MetaTags
        title={title}
        description={og_description}
        og_title={og_title}
        og_type={og_type}
        og_url={og_url}
        og_image={og_image}
        og_description={og_description}
        twitter_title={twitter_title}
        twitter_url={twitter_url}
        twitter_image={twitter_image}
        twitter_description={twitter_description}
      />
      <div className="flex w-full flex-col">
        {/* hero section */}
        <section
          className={cn(
            // 'hero gradient-fade-bg relative flex w-full flex-col items-center justify-start',
            'hero relative flex w-full flex-col items-center justify-start bg-[#0071E3]',
          )}
        >
          {/* <div className="z-10 container flex min-h-[80vh] flex-col items-center justify-center gap-4 px-4 pt-[64px]"> */}
          <div className="z-10 container flex flex-col items-center justify-center gap-4 px-4 pt-[64px]">
            <motion.div
              initial="hidden"
              animate="visible"
              variants={{
                hidden: {},
                visible: {
                  transition: {
                    staggerChildren: 0.15,
                  },
                },
              }}
              className="flex w-full flex-col items-start justify-center gap-8 md:items-center"
            >
              <motion.div
                variants={{
                  hidden: { opacity: 0, y: 20 },
                  visible: {
                    opacity: 1,
                    y: 0,
                    transition: { duration: 0.6, ease: 'easeOut' },
                  },
                }}
                className="pb-2"
              >
                <img src={icon} className="size-20" alt="picchat.ai" />
              </motion.div>
              <motion.div
                className="flex w-full flex-col justify-center text-left md:items-center md:text-center"
                variants={{
                  hidden: {},
                  visible: {
                    transition: {
                      staggerChildren: 0.1,
                    },
                  },
                }}
              >
                <motion.h1
                  // className="text-foreground/90 max-w-2xl text-4xl leading-[1] font-semibold tracking-tight text-balance md:max-w-2xl md:text-7xl"
                  className="text-background/90 dark:text-foreground/90 text-4xl leading-[1] font-semibold tracking-tight text-balance md:max-w-2xl md:text-5xl"
                  variants={{
                    hidden: { opacity: 0, y: 20 },
                    visible: {
                      opacity: 1,
                      y: 0,
                      transition: { duration: 0.6, ease: 'easeOut' },
                    },
                  }}
                >
                  Your image creation studio
                </motion.h1>
                <motion.h2
                  className="text-background/80 dark:text-foreground/80 mt-4 max-w-2xl text-xl leading-relaxed font-medium text-balance"
                  variants={{
                    hidden: { opacity: 0, y: 20 },
                    visible: {
                      opacity: 1,
                      y: 0,
                      transition: { duration: 0.6, ease: 'easeOut' },
                    },
                  }}
                >
                  Create, edit, organize, save and share{' '}
                  <Link
                    href="#"
                    className="text-background dark:text-foreground"
                  >
                    portraits
                  </Link>
                  ,{' '}
                  <Link
                    href="#"
                    className="text-background dark:text-foreground"
                  >
                    product shots
                  </Link>
                  ,{' '}
                  <Link
                    href="#"
                    className="text-background dark:text-foreground"
                  >
                    thumbnails
                  </Link>
                  , and{' '}
                  <Link
                    href="#"
                    className="text-background dark:text-foreground"
                  >
                    creative visuals
                  </Link>{' '}
                  powered by AI and{' '}
                  <Link
                    href="#"
                    className="text-background dark:text-foreground"
                  >
                    style recipes
                  </Link>
                  .
                </motion.h2>
              </motion.div>
              <motion.div
                className="flex flex-col items-start justify-center md:items-center"
                variants={{
                  hidden: {},
                  visible: {
                    transition: {
                      staggerChildren: 0.1,
                    },
                  },
                }}
              >
                <motion.div
                  className="flex flex-col items-start gap-4 sm:flex-row md:items-center"
                  variants={{
                    hidden: { opacity: 0, y: 20 },
                    visible: {
                      opacity: 1,
                      y: 0,
                      transition: { duration: 0.6, ease: 'easeOut' },
                    },
                  }}
                >
                  <a
                    href="https://apps.apple.com/app/picchat-ai/id6743186306"
                    className="min-w-[195px]"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <motion.div
                      className="bg-foreground text-background ring-foreground/10 flex h-14 items-center gap-3 rounded-lg px-6 py-3 shadow-lg ring-1 transition-shadow hover:shadow-xl"
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <FaApple className="h-6 w-6" />
                      <div className="flex-start flex flex-col text-left">
                        <span className="text-[10px] leading-tight">
                          Download on the
                        </span>
                        <span className="text-sm leading-tight font-semibold">
                          Mac App Store
                        </span>
                      </div>
                    </motion.div>
                  </a>
                  <PlatformsSelectorModal />
                  <PlatformsSelectorModal platform="linux" />
                </motion.div>
                {/* <div className="mx-auto flex w-max items-center justify-center gap-4 p-8">
                  <FaApple className="size-6" />
                  <FaWindows className="size-6" />
                  <FaLinux className="size-6" />
                </div> */}
                <motion.div
                  className="text-background/40 dark:text-foreground/40 pt-4 text-center text-sm"
                  variants={{
                    hidden: { opacity: 0, y: 20 },
                    visible: {
                      opacity: 1,
                      y: 0,
                      transition: { duration: 0.6, ease: 'easeOut' },
                    },
                  }}
                >
                  <span className="blockmd:inline">No subscriptions. </span>
                  <span className="blockmd:inline">No cloud lock-in. </span>
                  <span className="blockmd:inline">No slow web apps. </span>
                </motion.div>
              </motion.div>
            </motion.div>
          </div>

          <div className="relative flex w-full items-center justify-center">
            <motion.div
              className="relative z-10 container -mb-22 flex w-full flex-col items-start justify-center p-4 pt-16 md:-mb-44 md:items-center md:p-8 md:pt-22"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: '-50px' }}
              variants={{
                hidden: {},
                visible: {
                  transition: {
                    staggerChildren: 0.2,
                    delayChildren: 0.4,
                  },
                },
              }}
            >
              <motion.img
                src={picchatBulkActions}
                alt="picchat.ai - organize"
                className="ring-foreground/5 absolute left-8 container hidden h-auto w-1/2 overflow-hidden rounded-2xl shadow-2xl ring-1 md:flex"
                variants={{
                  hidden: { opacity: 0, x: -30, scale: 0.95 },
                  visible: {
                    opacity: 1,
                    x: 0,
                    scale: 1,
                    transition: { duration: 0.6, ease: 'easeOut' },
                  },
                }}
              />
              <motion.img
                src={picchatEditorCropResize}
                alt="picchat.ai - crop, reize, save and share"
                className="ring-foreground/5 absolute right-8 container hidden h-auto w-1/2 overflow-hidden rounded-2xl shadow-2xl ring-1 md:flex"
                variants={{
                  hidden: { opacity: 0, x: 30, scale: 0.95 },
                  visible: {
                    opacity: 1,
                    x: 0,
                    scale: 1,
                    transition: { duration: 0.6, ease: 'easeOut' },
                  },
                }}
              />
              <motion.img
                src={picchatNew}
                alt="picchat.ai - generate images with multiple models and style recipes"
                className="ring-foreground/5 z-10 container overflow-hidden rounded-2xl shadow-2xl ring-1 md:w-[60%]"
                variants={{
                  hidden: { opacity: 0, y: 30, scale: 0.95 },
                  visible: {
                    opacity: 1,
                    y: 0,
                    scale: 1,
                    transition: { duration: 0.6, ease: 'easeOut' },
                  },
                }}
              />
            </motion.div>
          </div>

          {/* <motion.div
            className="z-10 container -mb-22 flex flex-col items-start justify-center p-4 md:-mb-44 md:items-center"
            initial="hidden"
            animate="visible"
            variants={{
              hidden: { opacity: 0, y: 30 },
              visible: {
                opacity: 1,
                y: 0,
                transition: { duration: 0.8, ease: 'easeOut', delay: 0.4 },
              },
            }}
          >
            <img
              src={picchatCreate}
              alt="picchat.ai"
              className="ring-foreground/5 z-10 w-full overflow-hidden rounded-xl shadow-xs ring-1"
            />
          </motion.div> */}
          {/* <img
            src={macosTahoeBg}
            className="pointer-events-none absolute inset-0 h-full w-full object-cover object-center"
          /> */}
        </section>

        <section className="bg-foreground/2 flex min-h-screen w-full flex-col items-center justify-center">
          <div className="container flex flex-col items-center justify-start p-4 py-22 md:pt-64">
            <div className="flex w-full flex-col items-start justify-center gap-4 pt-12 md:items-center">
              <div className="md:text-center">
                <h3 className="not-prose max-w-2xl text-4xl leading-[1] font-semibold tracking-tight text-balance md:text-5xl">
                  {/* Visuals for social, professional, and personal use */}
                  Your all-in-one image creation tool
                </h3>
                <p className="text-muted-foreground mt-4 max-w-2xl text-lg leading-relaxed font-medium text-balance md:text-xl">
                  Whether you're producing thumbnails for YouTube, product
                  listings for Shopify, profile images for LinkedIn, or
                  marketing visuals for Instagram, picchat.ai adapts to your
                  goals and surfaces the right presets to make image generation
                  effortless.
                  {/* Whether you're creating YouTube thumbnails, Amazon product
                  images, LinkedIn headshots, Instagram posts, or Figma
                  placeholders, picchat.ai gives you the prompts and style
                  recipes to generate high-quality images for any professional
                  or personal project */}
                  {/* Whether you’re posting on social media, selling products
                  online, updating your dating profile, or building a
                  professional portfolio, picchat.ai gives you the tools to
                  generate high-quality images in moments. */}
                  {/* Choose the use cases
                  that matter to you, and the app prioritizes the right style
                  recipes to make image generation fast, simple, and precise */}
                  {/* picchat.ai adapts to your creative needs. Whether you're
                  building a professional portfolio, creating content for social
                  media, or designing product visuals, selecting your primary
                  use cases helps prioritize the right style recipes and prompts
                  in your workflow. */}
                </p>
              </div>
            </div>
          </div>
          <div className="no-scrollbar flex w-full min-w-full flex-1 snap-x snap-mandatory items-stretch justify-center gap-4 overflow-x-scroll py-12 pb-22">
            <div className="relative container flex w-full justify-start gap-8 px-4">
              {/* use cases grid */}
              {/* <div className="mt-12 grid w-full grid-cols-12 gap-8 py-12 pb-22"> */}
              <div className="flex w-full gap-8 md:grid md:grid-cols-12">
                {use_cases.map((useCase, index) => {
                  // Highlight main use cases with wider columns
                  const isMain = false
                  // useCase.id === 'professional_headshots' ||
                  // useCase.id === 'creator_influencer' ||
                  // useCase.id === 'product_ecommerce' ||
                  // useCase.id === 'dating_social'
                  const coverImageSrc = getCoverImageSrc(useCase.coverImage)
                  return (
                    <motion.div
                      key={useCase.id}
                      className={cn(
                        'ring-foreground/5 dark:bg-foreground/5 bg-background/5 relative flex flex-col gap-8 rounded-xl ring-1 transition-shadow hover:shadow-md',
                        isMain
                          ? 'col-span-12 md:col-span-6 lg:col-span-6'
                          : 'col-span-12 md:col-span-6 lg:col-span-3',
                        'min-w-[374px] snap-center md:min-w-full',
                      )}
                      initial="hidden"
                      whileInView="visible"
                      whileHover={{ scale: 1.02 }}
                      viewport={{ once: true, margin: '-50px' }}
                      variants={{
                        hidden: { opacity: 0, y: 20 },
                        visible: {
                          opacity: 1,
                          y: 0,
                          transition: {
                            duration: 0.5,
                            delay: index * 0.05,
                            ease: 'easeOut',
                          },
                        },
                      }}
                    >
                      <motion.div
                        // whileHover={{ scale: 1.02 }}
                        className="bg-muted/50 ring-foreground/5 relative z-1 aspect-3/4 overflow-hidden rounded-2xl shadow-lg ring-1"
                      >
                        <img
                          src={coverImageSrc}
                          alt={useCase.label}
                          className="h-full w-full object-cover"
                        />
                      </motion.div>
                      <motion.div
                        className={cn(
                          'pointer-events-none absolute inset-0 z-1 flex flex-col justify-end overflow-hidden rounded-2xl',
                          "before:absolute before:inset-0 before:mask-[linear-gradient(to_bottom,#0000_50%,black_85%)] before:backdrop-blur-lg before:content-['']",
                        )}
                      >
                        <div className="relative z-10 flex items-center justify-end p-8">
                          <div className="flex flex-1 flex-col overflow-hidden">
                            <motion.h3 className="truncate font-medium">
                              {useCase.label}
                            </motion.h3>
                            <motion.p className="text-foreground/60 mt-1 line-clamp-3 text-xs">
                              {useCase.description}
                            </motion.p>
                          </div>
                        </div>
                      </motion.div>
                    </motion.div>
                  )
                })}
              </div>
            </div>
          </div>
        </section>

        <section className="flex min-h-screen w-full flex-col items-center justify-center">
          <div className="container flex flex-col items-center justify-start p-4 py-22">
            <div className="flex w-full flex-col items-start justify-center gap-4 pt-12 md:items-center md:text-center">
              <div className="">
                <h3 className="not-prose max-w-2xl text-4xl leading-[1] font-semibold tracking-tight text-balance md:text-5xl">
                  Enhance your prompts for better, consistent images
                  {/* Enhance your prompts instantly */}
                  {/* Style Recipes - Generate consistent images in seconds */}
                  {/* Style Recipes - Better images, faster and easier */}
                  {/* Generate consistent images in seconds */}
                </h3>
                <p className="text-muted-foreground mt-4 max-w-2xl text-xl leading-relaxed font-medium text-balance">
                  Style Recipes enhance your prompts to produce images with
                  consistent composition, mood, and style, so you spend less
                  time tweaking and more time creating.
                  {/* Create better images faster, with consistent results.  */}
                  {/* Style Recipes enhance your prompts with tailored composition,
                  aspect ratios, and visual direction for YouTube thumbnails,
                  professional headshots, LinkedIn posts, and product visuals,
                  so you get polished outputs without trial and error. */}
                  {/* Reusable presets that enhance your AI image prompts. Choose
                  recipes built for YouTube thumbnails, professional headshots,
                  LinkedIn posts, and product visuals to add consistent
                  composition, aspect ratio, mood, and style to what you write. */}
                  {/* Style Recipes are reusable presets for AI image prompts.
                  Choose recipes built for YouTube thumbnails, professional
                  headshots, LinkedIn posts, and product visuals to apply
                  consistent composition, aspect ratio, mood, and style without
                  rewriting prompts each time. */}
                </p>
              </div>
            </div>
          </div>
          <div className="w-full flex-1">
            <div className="no-scrollbar flex w-full min-w-full flex-1 snap-x snap-mandatory items-stretch justify-center gap-4 overflow-x-scroll py-12 pb-22">
              <div className="relative container flex w-full justify-start gap-8 px-4">
                {styleRecipesSeed.map((recipe, index) => {
                  const coverImageSrc = getCoverImageSrc(recipe.coverImage)
                  return (
                    <StyleRecipeModal key={index} recipe={recipe}>
                      <motion.div
                        initial="offscreen"
                        whileInView="onscreen"
                        whileHover={{ scale: 1.02 }}
                        viewport={{
                          amount: 'some',
                        }}
                        variants={{
                          offscreen: {
                            opacity: 0,
                            translateX: 5,
                          },
                          onscreen: {
                            opacity: 1,
                            translateX: 0,
                          },
                        }}
                        className="relative flex w-full min-w-[374px] cursor-pointer snap-center flex-col gap-6"
                      >
                        <div className="ring-foreground/5 bg-foreground/5 aspect-square w-full overflow-hidden rounded-2xl shadow ring-1">
                          {coverImageSrc && (
                            <img
                              src={coverImageSrc}
                              alt={recipe.name}
                              className="h-full w-full object-cover"
                            />
                          )}
                        </div>
                        <div className="flex flex-col gap-2">
                          <h4 className="text-foreground font-semibold">
                            {recipe.name}
                          </h4>
                          <p className="text-muted-foreground">
                            {recipe.description}
                          </p>
                        </div>
                      </motion.div>
                    </StyleRecipeModal>
                  )
                })}
              </div>
            </div>
          </div>
        </section>

        <section className="flex w-full flex-col items-center justify-center bg-[#0071E3]">
          <div className="container flex flex-col items-center justify-start p-4 py-22">
            <div className="flex w-full flex-col items-start justify-center gap-4 md:items-center">
              <div className="md:text-center">
                <h3 className="not-prose max-w-2xl text-4xl leading-[1] font-semibold tracking-tight text-balance md:text-5xl">
                  Refine every image with ease
                </h3>
                <p className="text-background dark:text-foreground mt-4 max-w-2xl text-lg leading-relaxed font-medium text-balance md:text-xl">
                  Once your image is generated, the editor lets you iterate
                  across multiple models and providers. Edit, extend, crop, or
                  resize for any platform or use case, so every edit brings your
                  vision closer to perfection.
                </p>
              </div>
            </div>
          </div>
          <div className="container flex flex-col items-center justify-start p-4">
            <motion.img
              src={picchatEditorCropResize}
              alt="picchat.ai - editor"
              className="ring-foreground/5 h-auto w-full rounded-lg object-cover shadow ring-1"
              variants={{
                hidden: { opacity: 0, scale: 0.95 },
                visible: {
                  opacity: 1,
                  scale: 1,
                  transition: { duration: 0.5, ease: 'easeOut' },
                },
              }}
            />
          </div>

          {/* <div className="container flex flex-col items-center justify-center p-4 py-22"> */}
          <div className="container flex flex-col items-center justify-center p-4">
            <div className="grid w-full grid-cols-12 gap-8">
              {[
                {
                  title: 'Lightweight Image Editor',
                  description:
                    'A lightweight but powerful canvas for multi-turn editing, masking, inpainting, outpainting, and refinements',
                  image: picchatEditorCropResize,
                },
                {
                  title: 'Extend images',
                  description:
                    'Extend your images beyond original boundaries while matching composition, lighting and perspective.',
                  image: picchatExtendImages,
                },
                {
                  title: 'Organize - Bulk actions',
                  description:
                    'Search, filter, and curate your generated images in collections. Manage large creative libraries effortlessly',
                  image: picchatBulkActions,
                },
              ].map((item, index) => (
                <motion.div
                  key={`bento-${index}`}
                  className={cn(
                    // 'ring-foreground/5 relative flex flex-col gap-8 overflow-hidden rounded-2xl p-8 shadow-xs ring-1',
                    'ring-background/10 dark:ring-foreground/10 relative flex flex-col gap-8 overflow-hidden rounded-2xl p-8 shadow-xs ring-1',
                    'col-span-12 md:col-span-6 lg:col-span-4',
                    'dark:bg-foreground/5 bg-background/5',
                    'text-background dark:text-foreground',
                  )}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: false, margin: '-50px' }}
                  variants={{
                    hidden: { opacity: 0, y: 30 },
                    visible: {
                      opacity: 1,
                      y: 0,
                      transition: {
                        duration: 0.6,
                        ease: 'easeOut',
                        staggerChildren: 0.1,
                      },
                    },
                  }}
                >
                  <motion.header
                    className="flex w-full flex-col gap-4"
                    variants={{
                      hidden: {},
                      visible: {
                        transition: {
                          staggerChildren: 0.08,
                        },
                      },
                    }}
                  >
                    <motion.h3
                      className="font-semibold"
                      variants={{
                        hidden: { opacity: 0, y: 10 },
                        visible: {
                          opacity: 1,
                          y: 0,
                          transition: { duration: 0.4, ease: 'easeOut' },
                        },
                      }}
                    >
                      {item.title}
                    </motion.h3>
                    <motion.p
                      className="text-balance"
                      variants={{
                        hidden: { opacity: 0, y: 10 },
                        visible: {
                          opacity: 1,
                          y: 0,
                          transition: { duration: 0.4, ease: 'easeOut' },
                        },
                      }}
                    >
                      {item.description}
                    </motion.p>
                  </motion.header>
                  <div className="relative flex w-full items-center justify-start">
                    <motion.img
                      src={item.image}
                      alt={`picchat.ai - ${item.title}`}
                      className="ring-foreground/5 h-auto w-full rounded-lg object-cover shadow ring-1"
                      variants={{
                        hidden: { opacity: 0, scale: 0.95 },
                        visible: {
                          opacity: 1,
                          scale: 1,
                          transition: { duration: 0.5, ease: 'easeOut' },
                        },
                      }}
                    />
                  </div>
                </motion.div>
              ))}
            </div>

            <motion.div
              className="flex flex-col items-start justify-center md:items-center"
              variants={{
                hidden: {},
                visible: {
                  transition: {
                    staggerChildren: 0.1,
                  },
                },
              }}
            >
              <motion.div
                className="flex flex-col items-start gap-4 pt-12 sm:flex-row md:items-center md:pt-22"
                variants={{
                  hidden: { opacity: 0, y: 20 },
                  visible: {
                    opacity: 1,
                    y: 0,
                    transition: { duration: 0.6, ease: 'easeOut' },
                  },
                }}
              >
                <a
                  href="https://apps.apple.com/app/picchat-ai/id6743186306"
                  className="min-w-[195px]"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <motion.div
                    className="bg-foreground text-background ring-foreground/10 flex h-14 items-center gap-3 rounded-lg px-6 py-3 shadow-lg ring-1 transition-shadow hover:shadow-xl"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <FaApple className="h-6 w-6" />
                    <div className="flex-start flex flex-col text-left">
                      <span className="text-[10px] leading-tight">
                        Download on the
                      </span>
                      <span className="text-sm leading-tight font-semibold">
                        Mac App Store
                      </span>
                    </div>
                  </motion.div>
                </a>
                <PlatformsSelectorModal />
                <PlatformsSelectorModal platform="linux" />
              </motion.div>
              {/* <div className="mx-auto flex w-max items-center justify-center gap-4 p-8">
                  <FaApple className="size-6" />
                  <FaWindows className="size-6" />
                  <FaLinux className="size-6" />
                </div> */}
              <motion.div
                className="text-background/40 dark:text-foreground/40 pt-4 text-center text-sm"
                variants={{
                  hidden: { opacity: 0, y: 20 },
                  visible: {
                    opacity: 1,
                    y: 0,
                    transition: { duration: 0.6, ease: 'easeOut' },
                  },
                }}
              >
                <span className="blockmd:inline">No subscriptions. </span>
                <span className="blockmd:inline">No cloud lock-in. </span>
                <span className="blockmd:inline">No slow web apps. </span>
              </motion.div>
            </motion.div>
          </div>
        </section>

        {!beta && (
          <>
            {/* style recipes section */}
            {/* <section className="flex min-h-screen w-full flex-col items-center justify-center border py-32"> */}
            <section className="flex min-h-screen w-full flex-col items-center justify-center">
              <div className="container mt-22 flex flex-col items-center justify-center px-4 py-22 md:mt-44">
                {/* <div className="flex max-w-2xl flex-col">
              <h3 className="text-foreground/90 max-w-2xl text-4xl leading-[1] font-semibold tracking-tight text-balance md:max-w-2xl md:text-7xl">
                Style Recipes
              </h3>
              <p className="text-muted-foreground mt-4 max-w-2xl text-xl leading-relaxed font-medium text-balance">
                enhance your image prompts with style recipes tailored for your
                specific needs. custom/ create your own for faster workflows...
              </p>
            </div> */}
                <div className="flex max-w-5xl flex-col items-center justify-center text-center">
                  {/* <div className="text-muted-foreground mt-4 text-lg leading-relaxed font-medium text-balance">
                Presets
              </div> */}
                  <h3 className="text-foreground/90 text-4xl leading-[1] font-semibold tracking-tight text-balance md:text-7xl">
                    Enhance image prompts with{' '}
                    <span className="text-orange-400">style recipes</span>
                  </h3>
                </div>
              </div>
              <div className="no-scrollbar flex w-full min-w-full flex-1 snap-x snap-mandatory items-stretch justify-center gap-4 overflow-x-scroll py-12">
                <div className="relative container flex w-full justify-start gap-8 px-4">
                  {styleRecipeCategories.map((styleRecipeCategory, index) => (
                    <motion.div
                      key={index}
                      className="bg-foreground/5 dark:bg-foreground/1 ring-foreground/5 relative flex min-h-[500px] min-w-[80vw] snap-center flex-col rounded-4xl p-8 shadow-lg ring-1 md:min-h-[630px] md:min-w-[80vw]"
                      // bg-gradient-fade-alt
                    >
                      <div className="z-10 flex w-full flex-1 flex-col-reverse gap-8 md:flex-row">
                        <div className="flex flex-1 flex-col items-start justify-center gap-4">
                          <div className="z-10 flex w-full md:pb-12">
                            <div className="flex w-full flex-col">
                              <h3 className="text-foreground/90 max-w-2xl text-2xl leading-[1] font-semibold tracking-tight text-balance md:max-w-2xl">
                                {styleRecipeCategory.heading}
                              </h3>
                              <p className="text-muted-foreground mt-4 max-w-2xl leading-relaxed text-balance">
                                {styleRecipeCategory.description}
                              </p>
                            </div>
                          </div>
                          <ul className="hidden grid-cols-2 gap-4 md:grid">
                            {styleRecipeCategory.subcategories.map(
                              (item, itemIndex) => (
                                <li
                                  key={itemIndex}
                                  onClick={() => {
                                    setSelectedImageIndices((prev) => {
                                      const newIndices = [...prev]
                                      newIndices[index] = itemIndex
                                      return newIndices
                                    })
                                  }}
                                  className={cn(
                                    'text-foreground/90 hover:text-foreground cursor-pointer text-sm leading-relaxed font-medium text-balance transition-colors',
                                    selectedImageIndices[index] === itemIndex &&
                                      'text-foreground',
                                  )}
                                >
                                  {item.title}
                                </li>
                              ),
                            )}
                          </ul>
                          <div className="flex w-full py-2">
                            <Link
                              href="#"
                              className={cn(
                                buttonVariants({ variant: 'secondary' }),
                                'rounded-full',
                              )}
                            >
                              {styleRecipeCategory.cta}
                            </Link>
                          </div>
                        </div>
                        <div className="relative flex flex-1 flex-col items-center justify-center gap-4 overflow-visible pt-24 md:pt-0">
                          {/* 
                        - image stack 
                        styleRecipeCategory.subcategories.map -> item.image
                      */}
                          <div className="relative aspect-square w-full overflow-visible md:w-[80%] md:overflow-visible">
                            {[1, 2, 3, 4, 5, 6]
                              .map((imageId, imageIndex) => {
                                const selectedIndex =
                                  selectedImageIndices[index]
                                const stacked = true

                                // Calculate stack depth - distance from selected image
                                // Selected image is at depth 0 (front), others stack behind
                                const stackDepth = Math.abs(
                                  imageIndex - selectedIndex,
                                )
                                const depthFromFront = stacked ? stackDepth : 0

                                return { imageId, imageIndex, depthFromFront }
                              })
                              .sort(
                                (a, b) => b.depthFromFront - a.depthFromFront,
                              )
                              .map(
                                ({ imageId, imageIndex, depthFromFront }) => {
                                  const selectedIndex =
                                    selectedImageIndices[index]
                                  const stacked = true

                                  // Stack depth classes
                                  // Add horizontal and vertical offset to make stack more visible
                                  const depthClasses = stacked
                                    ? [
                                        'z-[50] scale-100 translate-y-0', // depth 0 (front)
                                        'z-[40] scale-[0.96] -translate-y-6', // depth 1
                                        'z-[30] scale-[0.92] -translate-y-12', // depth 2
                                        'z-[20] scale-[0.88] -translate-y-18', // depth 3
                                        'z-[10] scale-[0.84] -translate-y-24', // depth 4
                                        'z-0 scale-[0.80] -translate-y-30', // depth 5
                                      ][depthFromFront] ||
                                      'z-0 scale-[0.80] -translate-y-30'
                                    : ''

                                  // Stack opacity classes for images
                                  const imageOpacityClasses = stacked
                                    ? [
                                        '[&>img]:opacity-100', // depth 0
                                        '[&>img]:opacity-90', // depth 1
                                        '[&>img]:opacity-75', // depth 2
                                        '[&>img]:opacity-60', // depth 3
                                        '[&>img]:opacity-45', // depth 4
                                        '[&>img]:opacity-30', // depth 5
                                      ][depthFromFront] || '[&>img]:opacity-30'
                                    : ''

                                  const isSelected =
                                    imageIndex === selectedIndex

                                  // Use different images for variety
                                  const imageSources = [
                                    picchatEditor,
                                    picchatEditorDark,
                                    picchatLibrary,
                                    picchatCollectionsDark,
                                    picchatLibrary,
                                    picchatEditor,
                                  ]

                                  return (
                                    <motion.div
                                      key={imageIndex}
                                      layoutId={`image-${index}-${imageId}`}
                                      layout={true}
                                      whileHover={{
                                        scale: isSelected ? 1.02 : 1.01,
                                        transition: { duration: 0.2 },
                                      }}
                                      whileTap={{ scale: 0.98 }}
                                      transition={{
                                        layout: {
                                          duration: 0.4,
                                          ease: [0.4, 0, 0.2, 1],
                                        },
                                        opacity: { duration: 0.2 },
                                        scale: { duration: 0.2 },
                                      }}
                                      className={cn(
                                        'group/image-card bg-dotted ring-foreground/5 absolute right-0 bottom-0 left-0 mx-auto aspect-square overflow-hidden rounded-2xl shadow-lg ring-1',
                                        depthClasses,
                                        // imageOpacityClasses,
                                        {
                                          'ring-primary ring-2': isSelected,
                                        },
                                      )}
                                    >
                                      <img
                                        src={
                                          imageSources[
                                            imageIndex % imageSources.length
                                          ]
                                        }
                                        alt={`Image ${imageId}`}
                                        className="h-full w-full object-cover"
                                      />
                                    </motion.div>
                                  )
                                },
                              )}
                          </div>
                        </div>
                      </div>
                      {/* <img
                    src={macosTahoeBg}
                    className="pointer-events-none absolute inset-0 h-full w-full rounded-4xl object-cover object-center"
                  /> */}
                    </motion.div>
                  ))}
                </div>
              </div>
              {/* <div className="container mt-22 flex min-h-full flex-1 flex-col items-center justify-center border px-4 pb-22 md:mt-44">
            style recipes here....
          </div> */}
            </section>
            {/* core benefits section */}
            {false && (
              <section className="flex w-full flex-col items-center justify-center py-32">
                <div className="container flex flex-col items-center justify-center px-4">
                  <motion.div
                    className="relative w-full"
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, margin: '-100px' }}
                    variants={{
                      hidden: { opacity: 0 },
                      visible: {
                        opacity: 1,
                        transition: { duration: 0.6 },
                      },
                    }}
                  >
                    {/* Slider container with scroll snap */}
                    <div
                      ref={scrollContainerRef}
                      className="scrollbar-hide flex snap-x snap-mandatory gap-6 overflow-x-auto px-[7.5vw] md:px-[20vw] lg:px-[27.5vw]"
                    >
                      {coreBenefits.map((benefit, index) => {
                        const Icon = benefit.icon
                        return (
                          <div
                            key={index}
                            className="bg-card ring-foreground/5 flex min-w-[85vw] shrink-0 snap-center snap-always flex-col gap-8 rounded-3xl p-12 ring-1 md:min-w-[60vw] lg:min-w-[45vw] lg:p-16"
                          >
                            {/* Icon/Visual Section */}
                            <div className="flex shrink-0 items-center justify-center">
                              <div className="bg-foreground/5 dark:bg-foreground/10 flex h-40 w-40 items-center justify-center rounded-2xl md:h-48 md:w-48 lg:h-56 lg:w-56">
                                <Icon className="text-foreground/80 h-20 w-20 md:h-24 md:w-24 lg:h-28 lg:w-28" />
                              </div>
                            </div>

                            {/* Content Section */}
                            <div className="flex flex-1 flex-col items-center text-center md:items-start md:text-left">
                              <h3 className="text-foreground text-3xl font-semibold md:text-4xl lg:text-5xl">
                                {benefit.title}
                              </h3>
                              <p className="text-foreground/60 mt-6 text-lg leading-relaxed md:text-xl lg:text-2xl">
                                {benefit.description}
                              </p>
                            </div>
                          </div>
                        )
                      })}
                    </div>

                    {/* Navigation Dots */}
                    <div className="mt-12 flex items-center justify-center gap-2">
                      {coreBenefits.map((_, index) => (
                        <button
                          key={index}
                          onClick={() => scrollToSlide(index)}
                          className="focus:ring-foreground/20 relative h-2 w-2 rounded-full transition-all duration-300 focus:ring-2 focus:ring-offset-2 focus:outline-none"
                          aria-label={`Go to slide ${index + 1}`}
                        >
                          <motion.div
                            className="bg-foreground/40 absolute inset-0 rounded-full"
                            animate={{
                              scale: index === activeSlide ? 1.5 : 1,
                              opacity: index === activeSlide ? 1 : 0.4,
                            }}
                            transition={{ duration: 0.3 }}
                          />
                          {index === activeSlide && (
                            <motion.div
                              className="bg-foreground/80 absolute inset-0 rounded-full"
                              layoutId="activeDot"
                              transition={{ duration: 0.3 }}
                            />
                          )}
                        </button>
                      ))}
                    </div>

                    {/* Navigation Arrows */}
                    <button
                      onClick={() => {
                        const newIndex =
                          (activeSlide - 1 + coreBenefits.length) %
                          coreBenefits.length
                        scrollToSlide(newIndex)
                      }}
                      className="bg-foreground/10 hover:bg-foreground/20 focus:ring-foreground/20 absolute top-1/2 left-4 -translate-y-1/2 rounded-full p-3 transition-all focus:ring-2 focus:outline-none md:left-8"
                      aria-label="Previous slide"
                    >
                      <svg
                        className="text-foreground/80 h-5 w-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M15 19l-7-7 7-7"
                        />
                      </svg>
                    </button>
                    <button
                      onClick={() => {
                        const newIndex = (activeSlide + 1) % coreBenefits.length
                        scrollToSlide(newIndex)
                      }}
                      className="bg-foreground/10 hover:bg-foreground/20 focus:ring-foreground/20 absolute top-1/2 right-4 -translate-y-1/2 rounded-full p-3 transition-all focus:ring-2 focus:outline-none md:right-8"
                      aria-label="Next slide"
                    >
                      <svg
                        className="text-foreground/80 h-5 w-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 5l7 7-7 7"
                        />
                      </svg>
                    </button>
                  </motion.div>
                </div>
              </section>
            )}

            {/* CREATE */}
            <section className="bg-foreground/5 flex min-h-screen w-full flex-col items-center justify-center">
              <div className="container mt-22 flex flex-col items-center justify-center px-4 py-22 md:mt-44">
                <div className="flex max-w-5xl flex-col items-center justify-center text-center">
                  {/* <div className="text-muted-foreground mt-4 text-lg leading-relaxed font-medium text-balance">
                  Create
                </div> */}
                  <h3 className="text-foreground/90 text-4xl leading-[1] font-semibold tracking-tight text-balance md:text-7xl">
                    Easier way to prompt, enhance, and{' '}
                    <span className="text-orange-400">generate</span>
                  </h3>
                </div>
              </div>

              <motion.div
                className="z-10 container flex flex-col items-center justify-center p-4 pb-12"
                initial="hidden"
                animate="visible"
                variants={{
                  hidden: { opacity: 0, y: 30 },
                  visible: {
                    opacity: 1,
                    y: 0,
                    transition: { duration: 0.8, ease: 'easeOut', delay: 0.4 },
                  },
                }}
              >
                <img
                  src={picchatCreate}
                  // src={picchatCreateDark}
                  alt="picchat create page"
                  className="ring-foreground/5 z-10 w-full overflow-hidden rounded-xl shadow-xs ring-1"
                />
              </motion.div>
            </section>

            {/* EDITOR */}
            <section className="flex min-h-screen w-full flex-col items-center justify-center">
              <div className="container mt-22 flex flex-col items-center justify-center px-4 py-22 md:mt-44">
                <div className="flex max-w-5xl flex-col items-center justify-center text-center">
                  {/* <div className="text-muted-foreground mt-4 text-lg leading-relaxed font-medium text-balance">
                  CreEditate
                </div> */}
                  <h3 className="text-foreground/90 text-4xl leading-[1] font-semibold tracking-tight text-balance md:text-7xl">
                    Simple, multi-turn and granular editing in the{' '}
                    <span className="text-orange-400">editor</span>
                  </h3>
                </div>
              </div>

              <motion.div
                className="z-10 container flex flex-col items-center justify-center p-4 pb-12"
                initial="hidden"
                animate="visible"
                variants={{
                  hidden: { opacity: 0, y: 30 },
                  visible: {
                    opacity: 1,
                    y: 0,
                    transition: { duration: 0.8, ease: 'easeOut', delay: 0.4 },
                  },
                }}
              >
                <img
                  // src={picchatEditorDark}
                  src={picchatEditor}
                  alt="picchat create page"
                  className="ring-foreground/5 z-10 w-full overflow-hidden rounded-xl shadow-xs ring-1"
                />
              </motion.div>
            </section>

            {/* ORGANIZE */}
            <section className="flex min-h-screen w-full flex-col items-center justify-center">
              <div className="container mt-22 flex flex-col items-center justify-center px-4 py-22 md:mt-44">
                <div className="flex max-w-5xl flex-col items-center justify-center text-center">
                  {/* <div className="text-muted-foreground mt-4 text-lg leading-relaxed font-medium text-balance">
                  Organize / collections and whatnot
                </div> */}
                  <h3 className="text-foreground/90 text-4xl leading-[1] font-semibold tracking-tight text-balance md:text-7xl">
                    Search, sort, filter, hide and organize images in{' '}
                    <span className="text-orange-400">collections</span>
                  </h3>
                </div>
              </div>

              <motion.div
                className="z-10 container flex flex-col items-center justify-center p-4 pb-12"
                initial="hidden"
                animate="visible"
                variants={{
                  hidden: { opacity: 0, y: 30 },
                  visible: {
                    opacity: 1,
                    y: 0,
                    transition: { duration: 0.8, ease: 'easeOut', delay: 0.4 },
                  },
                }}
              >
                <img
                  // src={picchatEditorDark}
                  src={picchatCollectionsDark}
                  alt="picchat collections"
                  className="ring-foreground/5 z-10 w-full overflow-hidden rounded-xl shadow-xs ring-1"
                />
              </motion.div>
            </section>

            {/* features section */}
            <section
              id="features"
              className="flex w-full flex-col items-center justify-start"
            >
              {features.map((category, categoryIndex) => (
                <motion.div
                  key={`category-${categoryIndex}`}
                  className="container flex flex-col items-start justify-center gap-8 px-8 py-22"
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, margin: '-100px' }}
                  variants={{
                    visible: {
                      transition: {
                        staggerChildren: 0.1,
                      },
                    },
                  }}
                >
                  <motion.div
                    className="max-w-prose"
                    variants={{
                      hidden: { opacity: 0, y: 20 },
                      visible: {
                        opacity: 1,
                        y: 0,
                        transition: {
                          duration: 0.5,
                          staggerChildren: 0.1,
                        },
                      },
                    }}
                  >
                    <motion.div
                      variants={{
                        hidden: { opacity: 0, scale: 0.8 },
                        visible: {
                          opacity: 1,
                          scale: 1,
                          transition: { duration: 0.4 },
                        },
                      }}
                    >
                      <category.icon />
                    </motion.div>
                    <motion.h3
                      className="text-xl font-semibold"
                      variants={{
                        hidden: { opacity: 0, y: 10 },
                        visible: {
                          opacity: 1,
                          y: 0,
                          transition: { duration: 0.4 },
                        },
                      }}
                    >
                      {category.title}
                    </motion.h3>
                    <motion.div
                      className="text-foreground/60 text-sm text-balance"
                      variants={{
                        hidden: { opacity: 0, y: 10 },
                        visible: {
                          opacity: 1,
                          y: 0,
                          transition: { duration: 0.4 },
                        },
                      }}
                    >
                      <p>{category.description}</p>
                    </motion.div>
                  </motion.div>

                  <motion.div
                    className="grid w-full grid-cols-3 gap-8"
                    variants={{
                      hidden: {},
                      visible: {
                        transition: {
                          staggerChildren: 0.1,
                        },
                      },
                    }}
                  >
                    {category.items.map((item, itemIndex) => (
                      <motion.div
                        key={`${category.title.toLowerCase()}-item-${itemIndex}`}
                        className="ring-foreground/5 flex w-full flex-col gap-8 rounded-3xl p-8 ring-1"
                        variants={{
                          hidden: { opacity: 0, y: 30 },
                          visible: {
                            opacity: 1,
                            y: 0,
                            transition: {
                              duration: 0.5,
                              staggerChildren: 0.08,
                            },
                          },
                        }}
                      >
                        <motion.div
                          className="flex flex-col"
                          variants={{
                            hidden: {},
                            visible: {
                              transition: {
                                staggerChildren: 0.08,
                              },
                            },
                          }}
                        >
                          <motion.div
                            variants={{
                              hidden: { opacity: 0, scale: 0.8 },
                              visible: {
                                opacity: 1,
                                scale: 1,
                                transition: { duration: 0.3 },
                              },
                            }}
                          >
                            <item.icon />
                          </motion.div>
                          <motion.h4
                            className="text-foreground/80 text-lg font-semibold"
                            variants={{
                              hidden: { opacity: 0, y: 10 },
                              visible: {
                                opacity: 1,
                                y: 0,
                                transition: { duration: 0.3 },
                              },
                            }}
                          >
                            {item.title}
                          </motion.h4>
                          <motion.div
                            className="text-foreground/40 text-sm text-balance"
                            variants={{
                              hidden: { opacity: 0, y: 10 },
                              visible: {
                                opacity: 1,
                                y: 0,
                                transition: { duration: 0.3 },
                              },
                            }}
                          >
                            <p>{item.description}</p>
                          </motion.div>
                        </motion.div>
                        <motion.div
                          className="bg-foreground/5 aspect-square w-full rounded-2xl"
                          variants={{
                            hidden: { opacity: 0, scale: 0.95 },
                            visible: {
                              opacity: 1,
                              scale: 1,
                              transition: { duration: 0.4, delay: 0.2 },
                            },
                          }}
                        ></motion.div>
                      </motion.div>
                    ))}
                  </motion.div>
                </motion.div>
              ))}
            </section>
          </>
        )}
      </div>
    </>
  )
}
